
// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import MDButton from "components/MDButton";
import ButtonGroup from "@mui/material/ButtonGroup";
import CustomAlert from "components/CustomAlert";
import FormControl from "@mui/material/FormControl";
import MDInput from "components/MDInput";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import CircularProgress from '@mui/material/CircularProgress';
import MachineSkeleton from "components/MachineSkeleton";

// Data
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useEffect, useState } from "react";
import { MachineAPI } from "api/MachineAPI";
import { useNavigate } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";
import { useMaterialUIController, setLoading } from "context";
import { useParams } from "react-router-dom";
import dayjs from "dayjs";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from 'yup'


export default function CreateEditMachine() {
  const [controller, dispatch] = useMaterialUIController();
  const [machineCategories, setMachineCategories] = useState([]);
  const [plants, setPlants] = useState([]);
  const [alert, setAlert] = useState(false);
  const [alertContent, setAlertContent] = useState('');
  const [alertColor, setAlertColor] = useState('info');
  const navigate = useNavigate();
  const { loading } = controller;

  const { id } = useParams();
  const isCreateMode = !id;

  const formSchema = yup.object().shape({
    codigo: yup.string().required('El campo es requerido').max(30, 'El código no debe tener más de 30 caracteres'),
    id_planta: yup.number().required('El campo es requerido'),
    id_tipo_maquina: yup.number().required('El campo es requerido'),
    serie: yup.string().required('El campo es requerido').max(30, 'El número de serie no debe tener más de 30 caracteres'),
    fabricante: yup.string().required('El campo es requerido').max(30, 'El fabricante no debe tener más de 30 caracteres'),
    nombre: yup.string().required('El campo es requerido').max(30, 'El nombre no debe tener más de 30 caracteres'),
    fecha_fabricacion: yup.date().required('El campo es requerido'),
    fecha_instalacion: yup.date().required('El campo es requerido'),
  })

  const { handleSubmit, control, formState: { errors, isSubmitting }, setValue } = useForm({ resolver: yupResolver(formSchema), });

  const handleAlert = (content, color) => {
    setAlertContent(content);
    setAlertColor(color);
    setAlert(true)
  }

  const handleAlertClose = () => {
    setAlert(false);
  }

  const onSubmit = (data) => {
    return isCreateMode
      ? createMachine(data)
      : updateMachine(id, data);
  }

  const createMachine = (data) => {
    return MachineAPI.store(data)
      .then(response => {
        navigate('/machines', { state: response.message });

      }).catch(error => {
        if (error.response) {
          handleAlert(error.response.data.message, 'error')
          window.scrollTo(0, 0);
        }
      });
  }

  const updateMachine = (id, data) => {
    return MachineAPI.update(id, data)
      .then(response => {
        navigate('/machines', { state: response.message });

      }).catch(error => {
        if (error.response) {
          handleAlert(error.response.data.message, 'error')
          window.scrollTo(0, 0);
        }
      });
  }

  const getCategories = (signal) => {
    return MachineAPI.categories(signal)
      .then(response => {
        setMachineCategories(response.categories.id_tipo_maquina)
        setPlants(response.categories.plantas)
      }).catch(error => {
        if (error.response) {
          navigate('/not-found');
        }
      }).finally(() => {
        setLoading(dispatch, false)
      });
  }


  const getMachineAndCategories = (id, signal) => {
    return Promise.all([MachineAPI.show(id, signal), MachineAPI.categories(signal)])
      .then(function ([machine, categories]) {
        for (let [key, value] of Object.entries(machine.machine)) {
          setValue(key, value)
        }
        setMachineCategories(categories.categories.id_tipo_maquina)
        setPlants(categories.categories.plantas)
      })
      .catch(error => {
        navigate('/not-found');
      }).finally(() => {
        setLoading(dispatch, false)
      });
  }

  useEffect(() => {
    const abortController = new AbortController();
    const signal = abortController.signal

    setLoading(dispatch, true)
    !isCreateMode ? getMachineAndCategories(id, signal) : getCategories(signal);

    return () => {
      abortController.abort();
    };

  }, []);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Grid container justifyContent="center" pb={3} >
        <MDBox sx={{
          width: '80%',
        }}>
          <MDBox mb={4} px={2} >
            <CustomAlert severity={alertColor} message={alertContent} open={alert} onClose={handleAlertClose}></CustomAlert>
          </MDBox>
          <Card>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={12}>
                <MDBox p={2} lineHeight={0}>
                  <MDTypography variant="h5">Máquinas</MDTypography>
                  <MDTypography variant="button" color="text" fontWeight="regular">
                    Información de la máquina
                  </MDTypography>
                </MDBox>
                {loading ? <MachineSkeleton></MachineSkeleton> : (
                  <MDBox p={2} component="form" role="form" onSubmit={handleSubmit(onSubmit)}>
                    <Grid container p={2} spacing={3}>
                      <Grid item xs={6} sm={6}>
                        <Controller
                          control={control}
                          name="codigo"
                          render={({ field: { onChange, value } }) => (
                            <MDInput onChange={onChange} value={value || ''} label="Código" variant="standard" fullWidth />
                          )}
                        />
                        {errors.codigo && <MDTypography variant="button" color="error">{errors.codigo?.message}</MDTypography>}
                      </Grid>
                      <Grid item xs={6} sm={6}>
                        <FormControl fullWidth>
                          <Controller
                            name="id_planta"
                            control={control}
                            render={({ field: { onChange, value } }) => (
                              <Autocomplete
                                value={(plants.find((c) => c.id === value)) || null}
                                options={plants}
                                getOptionLabel={(plant) => plant.nombre}
                                renderInput={(params) => (
                                  <TextField {...params} label="Planta" variant="standard" />
                                )}
                                onChange={(event, item) => {
                                  onChange(item?.id)
                                }}
                              />
                            )}
                          />
                        </FormControl>
                        {errors.id_planta && <MDTypography variant="button" color="error">{errors.id_planta?.message}</MDTypography>}
                      </Grid>
                      <Grid item xs={6} sm={6}>
                        <FormControl fullWidth>
                          <Controller
                            name="id_tipo_maquina"
                            control={control}
                            render={({ field: { onChange, value } }) => (
                              <Autocomplete
                                value={(machineCategories.find((c) => c.id === value)) || null}
                                options={machineCategories}
                                getOptionLabel={(machineCategory) => machineCategory.descripcion}
                                renderInput={(params) => (
                                  <TextField {...params} label="Tipo de máquina" variant="standard" />
                                )}
                                onChange={(event, item) => {
                                  onChange(item?.id)
                                }}
                              />
                            )}

                          />
                        </FormControl>
                        {errors.id_tipo_maquina && <MDTypography variant="button" color="error">{errors.id_tipo_maquina?.message}</MDTypography>}
                      </Grid>
                      <Grid item xs={6} sm={6}>
                        <Controller
                          control={control}
                          name="serie"
                          render={({ field: { onChange, value } }) => (
                            <MDInput onChange={onChange} value={value || ''} label="Serie" variant="standard" fullWidth />
                          )}
                        />
                        {errors.serie && <MDTypography variant="button" color="error">{errors.serie?.message}</MDTypography>}
                      </Grid>
                      <Grid item xs={6} sm={6}>
                        <Controller
                          control={control}
                          name="fabricante"
                          render={({ field: { onChange, value } }) => (
                            <MDInput onChange={onChange} value={value || ''} label="Fabricante" variant="standard" fullWidth />
                          )}
                        />
                        {errors.fabricante && <MDTypography variant="button" color="error">{errors.fabricante?.message}</MDTypography>}
                      </Grid>
                      <Grid item xs={6} sm={6}>
                        <Controller
                          control={control}
                          name="nombre"
                          render={({ field: { onChange, value } }) => (
                            <MDInput onChange={onChange} value={value || ''} label="Nombre" variant="standard" fullWidth />
                          )}
                        />
                        {errors.nombre && <MDTypography variant="button" color="error">{errors.nombre?.message}</MDTypography>}
                      </Grid>
                      <Grid item xs={6} sm={6}>
                        <Controller
                          control={control}
                          name='fecha_fabricacion'
                          render={({ field: { onChange, value } }) =>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                              <DatePicker
                                value={dayjs(value) || null}
                                format="DD/MM/YYYY"
                                label="Fecha de Fabricación"
                                sx={{ width: "100%" }}
                                slotProps={{ textField: { variant: 'standard', } }}
                                onChange={(event) => { onChange(event) }}
                              />
                            </LocalizationProvider>
                          }
                        />
                        {errors.fecha_fabricacion && <MDTypography variant="button" color="error">{errors.fecha_fabricacion?.message}</MDTypography>}
                      </Grid>
                      <Grid item xs={6} sm={6}>
                        <Controller
                          control={control}
                          name='fecha_instalacion'
                          render={({ field: { onChange, value } }) =>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                              <DatePicker
                                value={dayjs(value) || null}
                                format="DD/MM/YYYY"
                                label="Fecha de Instalación"
                                sx={{ width: "100%" }}
                                slotProps={{ textField: { variant: 'standard', } }}
                                onChange={(event) => { onChange(event) }}
                              />
                            </LocalizationProvider>
                          }
                        />
                        {errors.fecha_instalacion && <MDTypography variant="button" color="error">{errors.fecha_instalacion?.message}</MDTypography>}
                      </Grid>
                    </Grid>
                    <MDBox sx={{ display: 'flex', justifyContent: 'flex-end' }} p={5}>
                      <ButtonGroup>
                        <MDButton variant="gradient" color="secondary" onClick={() => navigate('/machines')} >
                          Cancelar
                        </MDButton>
                        <MDButton variant="gradient" type="submit" color="info" disabled={isSubmitting}>
                          {isSubmitting ? <CircularProgress size={18} color="white" /> : 'Guardar'}
                        </MDButton>
                      </ButtonGroup>
                    </MDBox>
                  </MDBox>
                )}
              </Grid>
            </Grid>
          </Card>
        </MDBox>
      </Grid >
      <Footer />
    </DashboardLayout >
  );

}



